import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import { showToast } from '../../components/Toast';
import { DSAlert } from '../../design-system/primitives';
import MergeAccounts from './MergeAccounts';
import './mergeAccounts.css';

// The merge review, as a destination.
//
// It was a modal over the user list, which made two things awkward. Its back
// affordance was a close button dressed as navigation, so the browser back
// button left the page entirely rather than returning to the list. And the
// state lived in Users.js, so a reload lost it and the URL could not be shared
// or reopened.
//
// Everything it needs is in the query string, so this owns its own fetch: the
// page survives a refresh, and arriving from the work queue is the same code
// path as arriving from Reconcile.

function MergeReview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceId = searchParams.get('source');
  const targetId = searchParams.get('target');

  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (from, to) => {
    setError(null);
    setPreview(null);
    try {
      const res = await apiService.getMergePreview(from, to);
      setPreview(res.data);
      // Target wins where both have a value, so the surviving account keeps its
      // own details unless the admin says otherwise.
      setResult({
        sternUsername: res.data.target.sternUsername || res.data.source.sternUsername || '',
        preferredName: res.data.target.preferredName || res.data.source.preferredName || '',
        pronouns: res.data.target.pronouns || res.data.source.pronouns || '',
        hostProfile: res.data.target.hostProfile || res.data.source.hostProfile || ''
      });
    } catch (err) {
      // The real reason. A failure here can come from the request or from
      // reading the response, and a generic message told an admin nothing about
      // which, or what to tell whoever fixes it.
      setError(err.response?.data?.error || err.message || 'Could not load the merge preview');
    }
  }, []);

  useEffect(() => {
    if (!sourceId || !targetId) {
      setError('Two accounts are needed to merge. Pick them in Reconcile.');
      return;
    }
    load(sourceId, targetId);
  }, [sourceId, targetId, load]);

  const backToReconcile = () => navigate('/users?tab=reconcile');

  // Re-fetched rather than swapped in the browser: the preview is asymmetric.
  // The money summary, the alias check and the field decisions are all computed
  // for one direction, so flipping the two objects here would show a result the
  // server never agreed to. Navigating also keeps the URL honest about which
  // account survives.
  const swapDirection = () => {
    navigate(`/users/merge?source=${targetId}&target=${sourceId}`, { replace: true });
  };

  const confirm = async () => {
    try {
      setSubmitting(true);
      await apiService.mergeAccounts(sourceId, targetId, result);
      showToast('Accounts merged', 'success');
      backToReconcile();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to merge accounts', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // A permanent judgement, not a snooze. These two never appear as a suggested
  // duplicate again.
  const notDuplicates = async () => {
    try {
      await apiService.markNotDuplicates([Number(sourceId), Number(targetId)]);
      showToast('Recorded as two different people', 'success');
      backToReconcile();
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not record that', 'error');
    }
  };

  if (error) {
    return (
      <div className="merge-review">
        <DSAlert variant="error">{error}</DSAlert>
      </div>
    );
  }

  if (!preview || !result) return <div className="loading">Loading...</div>;

  return (
    <div className="merge-review">
      <MergeAccounts
        preview={preview}
        result={result}
        onResultChange={setResult}
        submitting={submitting}
        onCancel={backToReconcile}
        onSwapDirection={swapDirection}
        onConfirm={confirm}
        onNotDuplicates={notDuplicates}
      />
    </div>
  );
}

export default MergeReview;
